import classNames from 'classnames'
import _ from 'lodash'
import moment from 'moment-timezone'
import React from 'react'

import 'browser/components/atomic-elements/atoms/input/_time-input.scss'
import {
  ITextMaskInputProps,
  TextMaskInput,
} from 'browser/components/atomic-elements/atoms/input/text-mask-input'

export const timeMask = [/\d/, /\d/, ':', /\d/, /\d/]

type TimeUnitLimits = Partial<Record<string, number>>

function getTimeUnitLimit(limits: TimeUnitLimits, format: string): number {
  const limit = limits[format]
  if (limit === undefined) {
    throw new TypeError(`Unsupported time format: ${format}`)
  }
  return limit
}

export function createAutoCorrectedTimePipe(dateFormat = 'HH MM') {
  return (conformedValue: number | string) => {
    if (typeof conformedValue !== 'string') {
      throw new TypeError('conformedValue.split is not a function')
    }
    const indexesOfPipedChars: number[] = []
    const dateFormatArray = dateFormat.split(/[^HM]+/)
    const maxValue: TimeUnitLimits = { HH: 23, MM: 59 }
    const minValue: TimeUnitLimits = { HH: 0, MM: 0 }
    const conformedValueArr = conformedValue.split('')

    // Check first digit
    dateFormatArray.forEach((format) => {
      const position = dateFormat.indexOf(format)
      const maxFirstDigit = parseInt(getTimeUnitLimit(maxValue, format).toString().substr(0, 1), 10)
      if (parseInt(conformedValueArr[position], 10) > maxFirstDigit) {
        conformedValueArr[position + 1] = conformedValueArr[position]
        conformedValueArr[position] = '0'
        indexesOfPipedChars.push(position)
      }
    })

    // Check for invalid time
    const isInvalid = dateFormatArray.some((format) => {
      const position = dateFormat.indexOf(format)
      const length = format.length
      const textValue = conformedValue.substr(position, length).replace(/\D/g, '')
      const value = parseInt(textValue, 10)
      return (
        value > getTimeUnitLimit(maxValue, format) ||
        (textValue.length === length && value < getTimeUnitLimit(minValue, format))
      )
    })

    if (isInvalid) {
      return false
    }

    return {
      indexesOfPipedChars,
      value: conformedValueArr.join(''),
    }
  }
}

function getTimeLabel(hours: number, minutes: number, isMilitaryTime = true) {
  const minutesName = minutes < 10 ? `0${minutes}` : minutes
  if (isMilitaryTime) {
    const hoursName = hours < 10 ? `0${hours}` : hours
    return `${hoursName}:${minutesName}`
  } else {
    const hoursName = hours % 12 === 0 ? 12 : hours % 12
    const timeOfDay = hours < 12 ? 'AM' : 'PM'
    return `${hoursName}:${minutesName} ${timeOfDay}`
  }
}

function requireOnChange(onChange: ITimeInputProps['onChange']) {
  if (!onChange) {
    throw new TypeError('onChange is not a function')
  }
  return onChange
}

/**
 * Implements the time-input's `getNextStateFromProps`.
 */
export function deriveState(props: ITimeInputProps): ITimeInputState {
  const { value, timezoneId } = props

  const defaultState = {
    // moment.tz with an undefined zone is moment.utc
    date: (timezoneId === undefined ? moment.utc() : moment.tz(timezoneId)).startOf('day'),
    time: '',
  }

  if (value === undefined || _.isEmpty(value)) {
    return defaultState
  }

  const date =
    timezoneId === undefined
      ? moment.utc(value, moment.ISO_8601, true)
      : moment.tz(value, moment.ISO_8601, true, timezoneId)
  if (!date.isValid()) {
    return defaultState
  }

  const hours = date.hours()
  const minutes = date.minutes()
  const time = getTimeLabel(hours, minutes)
  return { date, time }
}

/**
 * @uiComponent
 */
export interface ITimeInputProps extends ITextMaskInputProps {
  onChange?(value?: string): void
  value?: string
  size?: string
  timezoneId?: string
}

export interface ITimeInputState {
  date: any
  time: string
}

export class TimeInput extends React.Component<ITimeInputProps, ITimeInputState> {
  static defaultProps: Partial<ITimeInputProps> = {
    timezoneId: moment.tz.guess(),
  }

  constructor(props: ITimeInputProps) {
    super(props)
    this.state = this.getNextStateFromProps(props)
  }

  public UNSAFE_componentWillReceiveProps(nextProps: ITimeInputProps) {
    // VD-10864
    // our date-time inputs only operate on date + hours + minutes
    // but someone up the stack is adding seconds + millis, which breaks the equality check
    const oldDateTime = moment(this.props.value)
    const newDateTime = moment(nextProps.value)

    if (!oldDateTime.isSame(newDateTime, 'minutes')) {
      this.setState(this.getNextStateFromProps(nextProps))
    }
  }

  private getNextStateFromProps(props: ITimeInputProps): ITimeInputState {
    return deriveState(props)
  }

  public render() {
    const { inputClassName } = this.props
    const { time } = this.state
    return (
      <TextMaskInput
        {...this.props}
        inputClassName={classNames('c-timeInput', inputClassName)}
        onChange={this.handleChange}
        guide={false}
        keepCharPositions={false}
        mask={timeMask}
        pipe={createAutoCorrectedTimePipe()}
        placeholder="hh:mm"
        value={time}
      />
    )
  }

  private handleChange = (value: string) => {
    const { onChange } = this.props
    this.setState({ time: value })
    if (_.isEmpty(value) || !value.match(/\d\d:\d\d/)) {
      return requireOnChange(onChange)(undefined)
    }
    const tokens = value.split(':')
    const hours = parseInt(tokens[0])
    const minutes = parseInt(tokens[1])
    const { date } = this.state
    date.hours(hours)
    date.minutes(minutes)

    requireOnChange(onChange)(date.toISOString())
  }
}
